import React, { useState, useEffect, useCallback } from 'react';
import WhatsAppSendButton from '../../components/WhatsAppSendButton';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { buildCftHtml } from './cftPrint';
import { anyCft, isAllRow, thicknessOptions, tKey, keyLabel } from '../../utils/cft';
import {
  ArrowLeft, Plus, Trash2, Save, Send, Printer, X, Lock, Unlock, Loader2, Ruler, Camera, ImageIcon, Edit, Users, CheckCircle2, AlertTriangle, Search, ClipboardCheck
} from 'lucide-react';

const OFFCUT_PERCENT = 30;
const r4 = n => Math.round((Number(n) || 0) * 10000) / 10000;
const fmt = n => Number(n || 0).toFixed(4);
// L × W × T × 0.00000003528 per piece × qty; an ALL CFT row (Issue / Return) carries its CFT typed in
const rowCft = anyCft;

const GROUPS = [
  { type: 'demand', label: 'Demand', color: 'blue' },
  { type: 'issue', label: 'Issue', color: 'green' },
  { type: 'return', label: 'Return', color: 'amber' },
];

// "Project Name (CODE)" - the name first, so the sheet says which project it is
const projectLabel = s => (s?.project_name
  ? `${s.project_name}${s.project_code ? ` (${s.project_code})` : ''}`
  : (s?.project_code || '—'));

const newRow = type => ({ _tmp: Math.random().toString(36).slice(2), row_type: type, is_all_cft: 0, label: '', length: '', width: '', thickness: '', qty: '', net_cft: '' });

export default function CFTSheetPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // /worker/my-cft/:id = a worker the approved sheet is assigned to (read-only + Tally details)
  const assignedView = location.pathname.includes('/my-cft/');
  const basePath = assignedView ? '/worker/my-cft' : (location.pathname.startsWith('/worker') ? '/worker/cft' : '/admin/cft');
  const [searchParams] = useSearchParams();
  const { canWrite, canEdit } = usePermission('cft');
  const { user } = useAuth();
  const { settings } = useSettings() || {};
  const isAdmin = user?.role === 'admin';
  // Settings → CFT Wood Stock: 'no' = a sheet that takes stock below zero can't be sent / approved
  const allowNegative = String(settings?.cft_allow_negative_stock || 'no').toLowerCase() === 'yes';

  const [sheet, setSheet] = useState(null);
  const [rows, setRows] = useState([]);
  const [woodType, setWoodType] = useState('');
  const [woodId, setWoodId] = useState('');          // wood type from the master
  const [woods, setWoods] = useState([]);
  useEffect(() => { if (!assignedView) api.get('/wood-types').then(r => setWoods(r.data.rows || [])).catch(() => {}); }, [assignedView]);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);

  const [avail, setAvail] = useState(null);          // { '38': 120.5, ALL: 40 } — stock before this sheet
  const [editMode, setEditMode] = useState(searchParams.get('edit') === '1');
  const [showAssign, setShowAssign] = useState(false);
  const [tally, setTally] = useState({ entered_in_tally: '', stock_journal_no: '', other_details: '' });
  const [savingTally, setSavingTally] = useState(false);

  const status = sheet?.status || 'draft';
  const isDraft = status === 'draft';
  const isLockedOld = status === 'submitted';             // locked before the approval flow
  // Draft sheets open read-only; Edit turns editing on. Locked after "Send for Approval".
  const editable = !assignedView && isDraft && canWrite && editMode;
  // Photos: while editing a draft; an admin can still fix them later
  const photosEditable = !assignedView && canWrite && ((isDraft && editMode) || (isAdmin && status !== 'pending'));
  const canAssign = !assignedView && status === 'approved' && (isAdmin || canEdit);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(assignedView ? `/cft-assigned/${id}` : `/cft-sheets/${id}`);
      setSheet(res.data);
      if (res.data.status !== 'draft') setEditMode(false);
      const mine = res.data.my_assignment || {};
      setTally({ entered_in_tally: mine.entered_in_tally || '', stock_journal_no: mine.stock_journal_no || '', other_details: mine.other_details || '' });
      setWoodType(res.data.wood_type || '');
      setWoodId(res.data.wood_type_id ? String(res.data.wood_type_id) : '');
      setRemarks(res.data.remarks || '');
      setPhotos(Array.isArray(res.data.cutting_list_photos) ? res.data.cutting_list_photos : []);
      setRows((res.data.rows || []).map(r => ({ ...r, _tmp: `db-${r.id}` })));
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load'); navigate(basePath); }
    finally { setLoading(false); }
  }, [id, navigate, basePath, assignedView]);
  useEffect(() => { load(); }, [load]);
  const sheetStatus = sheet?.status;
  useEffect(() => {
    if (assignedView || !woodId || !['draft', 'pending'].includes(sheetStatus)) { setAvail(null); return; }
    api.get('/wood-stock/available', { params: { wood_type_id: woodId, exclude_sheet: id } })
      .then(r => setAvail(r.data.buckets || {})).catch(() => setAvail(null));
  }, [woodId, id, assignedView, sheetStatus]);
  const currentWood = woods.find(w => String(w.id) === String(woodId));
  const hasThicknessList = !!currentWood;

  // Choose the wood type: its standard sizes become the Issue rows (asks before replacing filled rows)
  const pickWood = (id, force = false) => {
    const w = woods.find(x => String(x.id) === String(id));
    if (!force) {
      setWoodId(id); setWoodType(w ? w.name : '');
      if (w) {
        const opts = thicknessOptions(w);
        const bad = rows.filter(r => r.row_type !== 'demand' && !isAllRow(r) && Number(r.thickness) > 0 && !opts.includes(Number(r.thickness)));
        if (bad.length) {
          setRows(list => list.map(r => (bad.includes(r) ? { ...r, thickness: '' } : r)));
          toast(`${bad.length} Issue/Return row(s) had a thickness ${w.name} doesn't have — choose it again`, { icon: '⚠️' });
        }
      }
    }
    if (!w || !w.sizes?.length) return;
    const issue = rows.filter(r => r.row_type === 'issue');
    const filled = issue.filter(r => r.label || Number(r.length) || Number(r.width) || Number(r.thickness) || Number(r.qty) || Number(r.net_cft));
    if (filled.length && !window.confirm(`Replace the ${filled.length} Issue row(s) with the ${w.sizes.length} standard size(s) of ${w.name}? Quantities you typed in those rows will be cleared.`)) return;
    const fresh = w.sizes.map(z => ({ ...newRow('issue'), label: z.label || '', length: Number(z.length), width: Number(z.width), thickness: Number(z.thickness), qty: '' }));
    setRows(list => [...list.filter(r => r.row_type !== 'issue'), ...fresh]);
    toast.success(`Issue rows filled from ${w.name} — enter the quantities`);
  };
  const setRow = (tmp, patch) => setRows(list => list.map(r => r._tmp === tmp ? { ...r, ...patch } : r));
  const pickThickness = (r, v) => (v === 'ALL'
    ? setRow(r._tmp, { is_all_cft: 1, length: '', width: '', thickness: '', net_cft: r.net_cft || '' })
    : setRow(r._tmp, { is_all_cft: 0, thickness: v ? Number(v) : '' }));
  // Stock check per thickness: available + this sheet's returns − its issues
  const stockRows = (() => {
    if (!avail) return [];
    const m = new Map();
    rows.filter(r => r.row_type !== 'demand').forEach(r => {
      const all = isAllRow(r);
      if (!all && !(Number(r.thickness) > 0)) return;
      const k = tKey(all, r.thickness);
      if (!m.has(k)) m.set(k, { key: k, label: keyLabel(k), issue: 0, ret: 0 });
      m.get(k)[r.row_type === 'issue' ? 'issue' : 'ret'] += rowCft(r).net;
    });
    return [...m.values()].map(x => ({ ...x, available: r4(avail[x.key] || 0), after: r4((avail[x.key] || 0) + x.ret - x.issue) }));
  })();
  const shortRows = stockRows.filter(x => x.after < 0);
  const addRow = type => setRows(list => [...list, newRow(type)]);
  const delRow = tmp => setRows(list => list.filter(r => r._tmp !== tmp));

  // Live totals (mirrors server math exactly)
  const totals = (() => {
    const sum = t => rows.filter(r => r.row_type === t).reduce((a, r) => a + rowCft(r).net, 0);
    const demand = r4(sum('demand')), issue = r4(sum('issue')), ret = r4(sum('return'));
    const offcut = r4(demand * OFFCUT_PERCENT / 100);
    const required = r4(demand + offcut);
    const difference = r4(issue - required);
    const net = r4(difference - ret);
    return { demand, issue, ret, offcut, required, difference, net, consumption: r4(issue - ret), issueVsDemand: r4(issue - demand),
      label: net > 0 ? 'excess' : net < 0 ? 'shortage' : 'balanced' };
  })();

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const payload = { wood_type: woodType, wood_type_id: woodId || '', remarks, rows: rows.map(r => ({
        row_type: r.row_type, label: r.label, length: r.length, width: r.width, thickness: r.thickness, qty: r.qty,
        is_all_cft: r.row_type !== 'demand' && isAllRow(r) ? 1 : 0, net_cft: isAllRow(r) ? r.net_cft : undefined,
      })) };
      await api.put(`/cft-sheets/${id}`, payload);
      if (!silent) toast.success('Saved');
      await load();
      return true;
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); return false; }
    finally { setSaving(false); }
  };

  const sendForApproval = async () => {
    if (!window.confirm('Send this CFT sheet for approval? It will be locked until the admin approves or rejects it.')) return;
    setBusy(true);
    try {
      if (editMode && !(await save(true))) return;          // save pending edits first
      await api.post(`/cft-sheets/${id}/send-approval`);
      toast.success('Sent for approval');
      setEditMode(false);
      load();
    }
    catch (err) { toast.error(err.response?.data?.message || 'Could not send for approval'); }
    finally { setBusy(false); }
  };
  const cancelEdit = () => { setEditMode(false); load(); };
  const myDone = !!sheet?.my_assignment?.completed_at;
  const markComplete = async (done) => {
    if (done && !sheet?.my_assignment?.entered_in_tally) return toast.error('Save the Tally details first');
    if (!done && !window.confirm('Mark this sheet as pending again? You can then change the details.')) return;
    setSavingTally(true);
    try { const r = await api.post(`/cft-assigned/${id}/complete`, { done }); toast.success(r.data.message); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not update'); }
    finally { setSavingTally(false); }
  };
  const saveTally = async () => {
    if (!tally.entered_in_tally) return toast.error('Choose Yes or No for "Entered in Tally"');
    if (tally.entered_in_tally === 'yes' && !tally.stock_journal_no.trim()) return toast.error('Enter the Stock Journal Number');
    setSavingTally(true);
    try { await api.put(`/cft-assigned/${id}`, tally); toast.success('Details saved'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSavingTally(false); }
  };
  const reopen = async () => {
    if (!window.confirm('Reopen this sheet for editing?')) return;
    try { await api.post(`/cft-sheets/${id}/reopen`); toast.success('Reopened'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Reopen failed'); }
  };

  const uploadPhotos = async (files) => {
    const list = [...(files || [])].filter(f => f.type.startsWith('image/'));
    if (!list.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      list.forEach(f => fd.append('photos', f));
      const res = await api.post(`/cft-sheets/${id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotos(res.data.cutting_list_photos || []);
      toast.success(list.length > 1 ? `${list.length} photos uploaded` : 'Photo uploaded');
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed'); }
    finally { setUploading(false); }
  };
  const removePhoto = async (url) => {
    if (!window.confirm('Remove this cutting list photo?')) return;
    try {
      const res = await api.delete(`/cft-sheets/${id}/photos`, { params: { url } });
      setPhotos(res.data.cutting_list_photos || []);
    } catch (err) { toast.error(err.response?.data?.message || 'Remove failed'); }
  };

  // CFT sheet HTML from the current page state (used by Print and by WhatsApp)
  const buildHtml = () => {
    const tallyRows = assignedView ? [{ worker_name: user?.name, ...sheet.my_assignment }] : (sheet.assigned_workers || []);
    return buildCftHtml({ ...sheet, wood_type: woodType, remarks, tally_rows: tallyRows }, rows, totals, photos, settings || {}, window.location.origin);
  };

  const handlePrint = () => {
    if (!sheet) return;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups to print'); return; }
    w.document.write(buildHtml());
    w.document.close();
    w.focus();
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Loading…</div>;
  if (!sheet) return null;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(basePath)} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><ArrowLeft className="w-4 h-4 text-gray-600" /></button>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2"><Ruler className="w-5 h-5 text-blue-600" /> {sheet.sheet_no}</h1>
            <p className="text-sm text-gray-500">{projectLabel(sheet)}{sheet.item_name ? ` · ${sheet.item_name}` : ''}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={handlePrint} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm font-semibold text-gray-700"><Printer className="w-4 h-4" /> Print</button>
          {sheet && (
            <WhatsAppSendButton
              documentType="CFT_SHEET"
              referenceId={sheet.sheet_no || id}
              fileName={`CFT Sheet ${sheet.sheet_no || id}.pdf`}
              getHtml={async () => buildHtml()}
            />
          )}
          {!assignedView && isDraft && canWrite && !editMode && (
            <button onClick={() => setEditMode(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 text-sm font-semibold"><Edit className="w-4 h-4" /> Edit</button>
          )}
          {editable && <>
            <button onClick={cancelEdit} disabled={saving} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
            <button onClick={() => save()} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
          </>}
          {!assignedView && isDraft && canWrite && (
            <button onClick={sendForApproval} disabled={busy || (!allowNegative && shortRows.length > 0)}
              title={!allowNegative && shortRows.length ? 'Not enough stock — negative stock is off in Settings' : ''}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send for Approval</button>
          )}
          {canAssign && (
            <button onClick={() => setShowAssign(true)} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold"><Users className="w-4 h-4" /> Assign Workers</button>
          )}
          {!assignedView && (isLockedOld || status === 'approved') && isAdmin && <button onClick={reopen} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 text-sm font-semibold"><Unlock className="w-4 h-4" /> Reopen</button>}
        </div>
      </div>

      {isDraft && sheet.rejected_reason && !assignedView && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div><strong>Rejected:</strong> {sheet.rejected_reason}<div className="text-xs mt-1">Edit the sheet and send it for approval again.</div></div>
        </div>
      )}
      {isDraft && !editMode && !assignedView && canWrite && (
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600"><Edit className="w-4 h-4" /> Draft — tap <strong>Edit</strong> to change it, then <strong>Send for Approval</strong>.</div>
      )}
      {status === 'pending' && <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700"><Lock className="w-4 h-4" /> Waiting for approval — locked. The admin can approve or reject it from the Approvals page.</div>}
      {status === 'approved' && !assignedView && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          Approved{sheet.approved_by_name ? ` by ${sheet.approved_by_name}` : ''} — locked.{canAssign ? ' Use Assign Workers to send it to workers.' : ''}
        </div>
      )}
      {assignedView && !myDone && <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700"><Lock className="w-4 h-4" /> Assigned to you — the sheet is read-only. Fill in the Tally details below, then mark it complete.</div>}
      {assignedView && myDone && <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700"><CheckCircle2 className="w-4 h-4" /> You marked this sheet complete on {new Date(sheet.my_assignment.completed_at).toLocaleString()}.</div>}
      {isLockedOld && <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700"><Lock className="w-4 h-4" /> Submitted and locked.</div>}

      {assignedView && (
        <div className="bg-white rounded-xl border-2 border-blue-200 p-4 space-y-3">
          <h2 className="font-bold text-gray-800 flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-blue-600" /> Tally Details</h2>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Did you enter this in Tally? *</label>
            <div className="flex gap-2">
              {[['yes', 'Yes'], ['no', 'No']].map(([v, l]) => (
                <button key={v} type="button" disabled={myDone} onClick={() => setTally(t => ({ ...t, entered_in_tally: v }))}
                  className={`px-5 py-2 rounded-lg border text-sm font-semibold ${tally.entered_in_tally === v ? (v === 'yes' ? 'bg-green-600 text-white border-green-600' : 'bg-red-600 text-white border-red-600') : 'bg-white text-gray-600 border-gray-300'}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Stock Journal Number{tally.entered_in_tally === 'yes' ? ' *' : ''}</label>
              <input className="inp" disabled={myDone} maxLength={100} value={tally.stock_journal_no} onChange={e => setTally(t => ({ ...t, stock_journal_no: e.target.value }))} placeholder="e.g. SJ/26-27/0142" /></div>
            <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Other Details</label>
              <textarea className="inp" disabled={myDone} rows={3} maxLength={2000} value={tally.other_details} onChange={e => setTally(t => ({ ...t, other_details: e.target.value }))} placeholder="Anything else to note" /></div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-gray-400">{sheet.my_assignment?.details_updated_at ? `Last saved ${new Date(sheet.my_assignment.details_updated_at).toLocaleString()}` : 'Not saved yet'}</span>
            <div className="flex flex-wrap gap-2">
              {myDone ? (
                <button onClick={() => markComplete(false)} disabled={savingTally} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50">Undo Complete</button>
              ) : <>
                <button onClick={saveTally} disabled={savingTally} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">{savingTally ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Details</button>
                <button onClick={() => markComplete(true)} disabled={savingTally || !sheet.my_assignment?.entered_in_tally}
                  title={sheet.my_assignment?.entered_in_tally ? '' : 'Save the Tally details first'}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"><CheckCircle2 className="w-4 h-4" /> Mark as Complete</button>
              </>}
            </div>
          </div>
        </div>
      )}

      {!assignedView && status === 'approved' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-purple-50">
            <span className="font-bold text-purple-800 flex items-center gap-2"><Users className="w-4 h-4" /> Assigned Workers ({(sheet.assigned_workers || []).length})</span>
            {(sheet.assigned_workers || []).length > 0 && <span className="text-xs font-semibold text-gray-600">{sheet.assigned_workers.filter(w => w.completed_at).length} completed · {sheet.assigned_workers.filter(w => !w.completed_at).length} pending</span>}
          </div>
          {(sheet.assigned_workers || []).length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <th className="px-3 py-2">Worker</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Entered in Tally</th><th className="px-3 py-2">Stock Journal No.</th><th className="px-3 py-2">Other Details</th><th className="px-3 py-2">Updated</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {sheet.assigned_workers.map(w => (
                    <tr key={w.worker_id}>
                      <td className="px-3 py-2 font-medium text-gray-800">{w.worker_name}</td>
                      <td className="px-3 py-2">{w.completed_at
                        ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700" title={new Date(w.completed_at).toLocaleString()}>✓ Completed</span>
                        : <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">Pending</span>}</td>
                      <td className="px-3 py-2">{w.entered_in_tally ? <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${w.entered_in_tally === 'yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{w.entered_in_tally === 'yes' ? 'Yes' : 'No'}</span> : <span className="text-xs text-gray-400">Pending</span>}</td>
                      <td className="px-3 py-2 text-gray-700">{w.stock_journal_no || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-pre-wrap">{w.other_details || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-400">{w.details_updated_at ? new Date(w.details_updated_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="px-4 py-5 text-center text-xs text-gray-400">Not assigned to any worker yet{canAssign ? ' — use Assign Workers.' : '.'}</p>}
        </div>
      )}
      {showAssign && <AssignWorkersModal sheetId={id} current={(sheet.assigned_workers || []).map(w => w.worker_id)}
        onClose={() => setShowAssign(false)} onSaved={() => { setShowAssign(false); load(); }} />}

      <div className="bg-white rounded-xl border border-gray-200 p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Wood Type</label>
          {editable ? (
            <select className="inp" value={woodId} onChange={e => pickWood(e.target.value)}>
              <option value="">{woodType && !woodId ? `${woodType} (typed earlier — pick from the list)` : '— Select wood —'}</option>
              {woods.filter(w => w.is_active || String(w.id) === woodId).map(w => <option key={w.id} value={w.id}>{w.name}{w.sizes?.length ? ` (${w.sizes.length} sizes)` : ''}</option>)}
            </select>
          ) : <input className="inp" disabled value={woodType} placeholder="—" />}
          {editable && woodId && woods.find(w => String(w.id) === woodId)?.sizes?.length > 0 && (
            <button type="button" onClick={() => pickWood(woodId, true)} className="mt-1 text-[11px] font-semibold text-purple-700 hover:underline">Fill Issue rows with this wood's standard sizes</button>
          )}</div>
        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Remarks</label>
          <input className="inp" disabled={!editable} value={remarks} onChange={e => setRemarks(e.target.value)} /></div>
      </div>

      {/* Cutting list photos */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-gray-100 bg-gray-50">
          <span className="font-bold text-gray-700 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-blue-600" /> Cutting List Photos <span className="text-xs font-normal text-gray-500">({photos.length})</span></span>
          {photosEditable && (
            <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold cursor-pointer ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
              {uploading ? 'Uploading…' : 'Upload Photo'}
              <input type="file" accept="image/*" multiple className="hidden" disabled={uploading}
                onChange={e => { uploadPhotos(e.target.files); e.target.value = ''; }} />
            </label>
          )}
        </div>
        {photos.length ? (
          <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {photos.map(u => (
              <div key={u} className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 aspect-[4/3]">
                <img src={u} alt="Cutting list" className="w-full h-full object-cover cursor-zoom-in" onClick={() => setViewPhoto(u)} />
                {photosEditable && (
                  <button onClick={() => removePhoto(u)} title="Remove photo"
                    className="absolute top-1.5 right-1.5 p-1 rounded-full bg-white/90 text-red-600 shadow hover:bg-white"><X className="w-3.5 h-3.5" /></button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="px-4 py-5 text-center text-xs text-gray-400">No cutting list photo yet{photosEditable ? ' — tap Upload Photo to add one' : ''}.</p>
        )}
      </div>
      {viewPhoto && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setViewPhoto(null)}>
          <img src={viewPhoto} alt="Cutting list" className="max-w-full max-h-full rounded-lg" />
          <button className="absolute top-4 right-4 p-2 rounded-full bg-white/90"><X className="w-5 h-5" /></button>
        </div>
      )}

      {!assignedView && stockRows.length > 0 && (
        <div className={`rounded-xl border p-3 text-sm ${shortRows.length ? 'bg-red-50 border-red-200 text-red-800' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
          <div className="font-bold flex items-center gap-2 mb-1.5">{shortRows.length ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4 text-green-600" />}
            {shortRows.length
              ? (allowNegative
                ? `Not enough ${woodType} stock for ${shortRows.map(x => x.label).join(', ')} — negative stock is allowed in Settings, so the sheet can still be sent; the approver will see this warning.`
                : `Not enough ${woodType} stock for ${shortRows.map(x => x.label).join(', ')} — reduce the Issue rows or add stock. The sheet can't be sent for approval until then.`)
              : `${woodType} stock is enough for this sheet`}</div>
          <div className="flex flex-wrap gap-2">
            {stockRows.map(x => (
              <span key={x.key} className={`px-2 py-1 rounded-lg border text-xs ${x.after < 0 ? 'bg-white border-red-300' : 'bg-white border-gray-200'}`}>
                <b>{x.label}</b> · available {fmt(x.available)} · issue {fmt(x.issue)}{x.ret ? ` · return ${fmt(x.ret)}` : ''} · <b className={x.after < 0 ? 'text-red-700' : 'text-green-700'}>left {fmt(x.after)}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      {GROUPS.map(g => {
        const groupRows = rows.filter(r => r.row_type === g.type);
        const groupTotal = groupRows.reduce((a, r) => a + rowCft(r).net, 0);
        return (
          <div key={g.type} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className={`flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-${g.color}-50`}>
              <span className={`font-bold text-${g.color}-700`}>{g.label} <span className="text-xs font-normal text-gray-500">({groupRows.length} rows · {fmt(groupTotal)} CFT)</span></span>
              {editable && <button onClick={() => addRow(g.type)} className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-gray-300 text-xs font-semibold text-gray-600"><Plus className="w-3.5 h-3.5" /> Add Row</button>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[680px]">
                <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <th className="px-3 py-2">Label</th><th className="px-3 py-2 text-right">L (mm)</th><th className="px-3 py-2 text-right">W (mm)</th>
                  <th className="px-3 py-2 text-right">T (mm)</th><th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">CFT/pc</th><th className="px-3 py-2 text-right">Total CFT</th><th></th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {groupRows.map(r => {
                    const c = rowCft(r);
                    const all = isAllRow(r);
                    const useList = g.type !== 'demand' && hasThicknessList;
                    const opts = useList ? thicknessOptions(currentWood, [r.thickness]) : [];
                    const k = all ? 'ALL' : (Number(r.thickness) > 0 ? tKey(false, r.thickness) : '');
                    return (
                      <tr key={r._tmp}>
                        <td className="px-3 py-1.5"><input className="cell" disabled={!editable} value={r.label || ''} onChange={e => setRow(r._tmp, { label: e.target.value })} placeholder="Part / plank" /></td>
                        <td className="px-3 py-1.5"><input className="cell num" type="number" disabled={!editable || all} value={all ? '' : r.length} placeholder={all ? '—' : ''} onChange={e => setRow(r._tmp, { length: e.target.value })} /></td>
                        <td className="px-3 py-1.5"><input className="cell num" type="number" disabled={!editable || all} value={all ? '' : r.width} placeholder={all ? '—' : ''} onChange={e => setRow(r._tmp, { width: e.target.value })} /></td>
                        <td className="px-3 py-1.5 min-w-[104px]">
                          {useList ? <>
                            <select className="cell" disabled={!editable} value={k} onChange={e => pickThickness(r, e.target.value)}>
                              <option value="">— T —</option>
                              {opts.map(t => <option key={t} value={String(t)}>{t} mm</option>)}
                              <option value="ALL">ALL CFT</option>
                            </select>
                            {avail && k && <div className={`text-[10px] text-right mt-0.5 ${(avail[k] || 0) < 0 ? 'text-red-600' : 'text-gray-400'}`}>avail {fmt(avail[k] || 0)}</div>}
                          </> : <input className="cell num" type="number" disabled={!editable} value={r.thickness} onChange={e => setRow(r._tmp, { thickness: e.target.value })} />}
                        </td>
                        <td className="px-3 py-1.5"><input className="cell num" type="number" disabled={!editable} value={r.qty} onChange={e => setRow(r._tmp, { qty: e.target.value })} /></td>
                        <td className="px-3 py-1.5 text-right text-gray-500">{all ? '—' : fmt(c.gross)}</td>
                        <td className="px-3 py-1.5 text-right font-semibold">{all
                          ? <input className="cell num font-semibold" type="number" min="0" step="0.0001" disabled={!editable} value={r.net_cft ?? ''} placeholder="CFT" onChange={e => setRow(r._tmp, { net_cft: e.target.value })} />
                          : fmt(c.net)}</td>
                        <td className="px-3 py-1.5 text-right">{editable && <button onClick={() => delRow(r._tmp)} className="p-1 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                      </tr>
                    );
                  })}
                  {!groupRows.length && <tr><td colSpan={8} className="px-3 py-4 text-center text-gray-300 text-xs">No {g.label.toLowerCase()} rows</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Summary */}
      <div className="bg-white rounded-xl border-2 border-blue-500 overflow-hidden">
        <SummaryRow k="Total Demand CFT" v={totals.demand} />
        <SummaryRow k="Offcut (30% — fixed)" v={totals.offcut} />
        <SummaryRow k="Total Required CFT (Demand + 30%)" v={totals.required} strong />
        <SummaryRow k="Total Issue CFT" v={totals.issue} />
        <SummaryRow k="Difference (Issue − Required)" v={totals.difference} />
        <SummaryRow k="Total Return CFT" v={totals.ret} />
        <SummaryRow k="Net Consumption (Issue − Return)" v={totals.consumption} />
        <SummaryRow k="Issue − Demand" v={totals.issueVsDemand} />
        <div className={`flex items-center justify-between px-4 py-3 text-white font-bold text-lg ${totals.net > 0 ? 'bg-green-600' : totals.net < 0 ? 'bg-red-600' : 'bg-blue-600'}`}>
          <span>NET DIFFERENCE {totals.net > 0 ? '— EXCESS' : totals.net < 0 ? '— SHORTAGE' : ''}</span>
          <span>{fmt(totals.net)}</span>
        </div>
      </div>
      <p className="text-xs text-gray-400 italic">CFT = L × W × T × 0.00000003528 (per piece) × Qty. Offcut fixed at 30% (shown for planning, never taken out of stock). Difference = Issue − Required. Net = Difference − Return. Positive = excess, negative = shortage. Issue and Return thickness come from the wood's list; ALL CFT rows take the CFT typed in. Stock changes only when the sheet is approved.</p>

      <style>{`
        .inp{width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:8px;font-size:13px;} .inp:disabled{background:#f9fafb;color:#4b5563;}
        .cell{width:100%;padding:5px 7px;border:1px solid #e5e7eb;border-radius:6px;font-size:12px;} .cell:disabled{background:#f9fafb;border-color:transparent;} .cell.num{text-align:right;}
        .inp:focus,.cell:focus{outline:2px solid #bfdbfe;border-color:#3b82f6;}
      `}</style>
    </div>
  );
}

// Multi-select of workers for an approved sheet
function AssignWorkersModal({ sheetId, current, onClose, onSaved }) {
  const [workers, setWorkers] = useState([]);
  const [picked, setPicked] = useState(new Set(current));
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/cft-sheets/${sheetId}/assignable-workers`)
      .then(r => setWorkers(r.data || [])).catch(err => toast.error(err.response?.data?.message || 'Failed to load workers'))
      .finally(() => setLoading(false));
  }, [sheetId]);
  const shown = workers.filter(w => `${w.name} ${w.username}`.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = wid => setPicked(s => { const n = new Set(s); n.has(wid) ? n.delete(wid) : n.add(wid); return n; });
  const allShown = shown.length > 0 && shown.every(w => picked.has(w.id));
  const toggleAll = () => setPicked(s => { const n = new Set(s); shown.forEach(w => (allShown ? n.delete(w.id) : n.add(w.id))); return n; });
  const save = async () => {
    setSaving(true);
    try { const r = await api.put(`/cft-sheets/${sheetId}/workers`, { worker_ids: [...picked] }); toast.success(r.data.message || 'Saved'); onSaved(); }
    catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><Users className="w-5 h-5 text-purple-600" /> Assign Workers</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 border-b border-gray-100 space-y-2">
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search workers" className="flex-1 text-sm outline-none" />
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">{picked.size} selected</span>
            {shown.length > 0 && <button onClick={toggleAll} className="font-semibold text-purple-700">{allShown ? 'Clear these' : 'Select all'}</button>}
          </div>
        </div>
        <div className="overflow-y-auto flex-1 px-2 py-2">
          {loading ? <p className="text-center text-sm text-gray-400 py-6">Loading…</p>
            : !shown.length ? <p className="text-center text-sm text-gray-400 py-6">No workers found</p>
            : shown.map(w => (
              <label key={w.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer ${picked.has(w.id) ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                <input type="checkbox" checked={picked.has(w.id)} onChange={() => toggle(w.id)} className="w-4 h-4 accent-purple-600" />
                <span className="flex-1 min-w-0"><span className="block text-sm font-medium text-gray-800 truncate">{w.name}</span><span className="block text-xs text-gray-400 truncate">@{w.username}</span></span>
              </label>
            ))}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:bg-gray-100">Cancel</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold disabled:opacity-50">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save ({picked.size})</button>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ k, v, strong }) {
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 border-b border-gray-100 text-sm ${strong ? 'font-bold text-gray-800 bg-blue-50/50' : 'text-gray-600'}`}>
      <span>{k}</span><span>{fmt(v)}</span>
    </div>
  );
}
